import { useState } from "react";
import { useGetOne, useNotify, useRefresh, useUpdate } from "ra-core";
import { Linkedin, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { Company, Contact } from "../types";

// LDF: LinkedIn-Profil von Hand finden – öffnet die LinkedIn-Personensuche (im eigenen, angemeldeten Browser)
// mit Name und Firma; den gefundenen Profil-Link hier einfügen und speichern. Kein automatischer Zugriff auf LinkedIn.

const PROFILE_RE = /^https?:\/\/(?:[a-z]{2,3}\.)?linkedin\.com\/in\/[A-Za-z0-9\-_%]+\/?/i;

export const LinkedInFinder = ({ record }: { record: Contact }) => {
  const { data: company } = useGetOne<Company>("companies", { id: record.company_id as number }, { enabled: record.company_id != null });
  const [url, setUrl] = useState("");
  const [open, setOpen] = useState(false);
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (record.linkedin_url || company?.contact_blocked) return null;

  const person = [record.first_name, record.last_name].filter(Boolean).join(" ");
  const companyName = company?.name?.replace(/\b(GmbH|AG|UG|KG|LLP|e\.V\.|Botschaft der|Botschaft von)\b/g, "").trim();
  const searchUrl = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent([person, companyName].filter(Boolean).join(" "))}`;

  const save = () => {
    const match = url.trim().match(PROFILE_RE);
    if (!match) {
      notify("Bitte einen Profil-Link wie https://www.linkedin.com/in/… einfügen", { type: "warning" });
      return;
    }
    update(
      "contacts",
      { id: record.id, data: { linkedin_url: match[0].replace(/\/$/, "") }, previousData: record },
      {
        onSuccess: () => {
          notify("LinkedIn-Profil gespeichert", { type: "success" });
          setUrl("");
          setOpen(false);
          refresh();
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-1 py-1">
      <div className="flex items-center gap-2 text-sm">
        <Linkedin className="h-4 w-4 text-muted-foreground" />
        <a
          href={searchUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 underline hover:no-underline text-muted-foreground"
          title="Öffnet die LinkedIn-Suche in einem neuen Tab"
        >
          <Search className="h-3 w-3" /> Auf LinkedIn suchen
        </a>
        {!open && (
          <button type="button" className="text-xs underline cursor-pointer text-muted-foreground" onClick={() => setOpen(true)}>
            Link einfügen
          </button>
        )}
      </div>
      {open && (
        <div className="flex gap-1 pl-6">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder="https://www.linkedin.com/in/…"
            className="h-7 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs"
          />
          <Button size="sm" className="h-7 px-2 text-xs" disabled={isPending || !url.trim()} onClick={save}>
            Speichern
          </Button>
        </div>
      )}
    </div>
  );
};
